import {
  ExpandLess,
  ExpandMore,
  Dashboard,
  School,
  PersonAdd,
  Lock,
  Assessment,
  BarChart,
  Quiz,
  Settings,
  People,
  TrendingUp,
  Logout,
  AccountCircle,
  ReceiptLong,
  Home,
  Notifications,
  Inbox,
  MailOutline,
  EmojiEvents,
  Menu as MenuIcon,
  FactCheck,
  Insights,
  MenuBook,
  ShoppingBag,
  AutoAwesome,
  Event,
} from "@mui/icons-material";
import {
  AppBar,
  Box,
  Collapse,
  CssBaseline,
  Drawer,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Badge,
  IconButton,
  Tooltip,
  Switch,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import ParentWelcomeTour from "../../components/ParentWelcomeTour";
import { toast } from "react-toastify";
import logout from "../../logout";
import CapitalizeFirstLetter from "../../CapitalizeFirstLetter";
import api from "../../api";
import { PortalToggleSwitch } from "../../components";
import TesterPortalSwitcher from "../../components/TesterPortalSwitcher";
import AssistantDrawer from "../../components/PortalAssistant/AssistantDrawer";
import { useParentSessionIdle } from "../../hooks/useParentSessionIdle";
import useExamMode from "../../hooks/useExamMode";
import usePracticeModeVisible from "../../hooks/usePracticeModeVisible";
import { navIconTileSx } from "../../components/navIconTile";

/** One colour per sidebar section, so the icons read at a glance (see navIconTile). */
const PARENT_ITEM_COLORS = {
  Dashboard: "#2196f3",
  Students: "#4caf50",
  Progress: "#ff9800",
  "Student Results": "#e91e63",
  Billing: "#00897b",
  "My Purchases": "#8e24aa",
  Notifications: "#f44336",
  Messages: "#0ea5e9",
  Announcements: "#ff5722",
  Certificates: "#d4a017",
  Setting: "#607d8b",
};
const itemColor = (item) => item.color || PARENT_ITEM_COLORS[item.label] || "#667eea";
const drawerWidth = 240;
const MINI_DRAWER_WIDTH = 64;
const PRACTICE_MODE_TOOLTIP =
  "Practice mode applies to mock exams, practice papers, and worksheets. Off by default — turn it on to use Check answer.";

const parentDrawerPaperSx = (width, appBarHeight) => ({
  width,
  boxSizing: "border-box",
  mt: `${appBarHeight}px`,
  maxHeight: `calc(100vh - ${appBarHeight}px)`,
  // dvh so iOS Safari's bottom toolbar doesn't cover the end of the menu.
  "@supports (height: 100dvh)": { maxHeight: `calc(100dvh - ${appBarHeight}px)` },
  background: "linear-gradient(180deg, #f8f9fa 0%, #e9ecef 100%)",
  borderRight: "1px solid #dee2e6",
  boxShadow: "2px 0 10px rgba(0,0,0,0.1)",
  overflowY: "auto",
  overflowX: "hidden",
  transition: "all 0.3s ease",
  "&::-webkit-scrollbar": { width: "8px" },
  "&::-webkit-scrollbar-track": { background: "rgba(0,0,0,0.05)", borderRadius: "10px" },
  "&::-webkit-scrollbar-thumb": {
    background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.2)",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    background: "linear-gradient(180deg, #3B2A9F 0%, #D62926 100%)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "#667eea rgba(0,0,0,0.05)",
});

const STUDENT_SESSION_KEY = "studentSessionForSwitch";
const ACTIVE_PARENT_STUDENT_KEY = "parentActiveStudentIdForExamMode";

const ParentLayout = () => {
  useParentSessionIdle();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isCompactAppBar = useMediaQuery(theme.breakpoints.down("sm"));
  const appBarHeight = isCompactAppBar ? 56 : 70;
  const [openMenus, setOpenMenus] = useState({});
  const loading = false;
  const [userData, setUserData] = useState(null); // user data from localStorage
  const [announcementCount, setAnnouncementCount] = useState(0); // announcement count for badge
  const [notificationCount, setNotificationCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileFull, setMobileFull] = useState(false);
  const [switchToStudentLoading, setSwitchToStudentLoading] = useState(false);
  const [activeStudentId, setActiveStudentId] = useState(() => {
    const stateId = location.state?.studentId;
    if (stateId) return Number(stateId);
    const cached = sessionStorage.getItem(ACTIVE_PARENT_STUDENT_KEY);
    return cached ? Number(cached) : null;
  });
  const { mockExamsMode, setMode, settingMode } = useExamMode(activeStudentId || null);
  const practiceModeVisible = usePracticeModeVisible();
  const [parentPermissions, setParentPermissions] = useState(() => {
    try {
      const raw = localStorage.getItem("parent-permissions");
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  /** False until first sync when parent-permissions was missing (e.g. parent-sso). */
  const [hasCourseEnrollment, setHasCourseEnrollment] = useState(
    () => localStorage.getItem("parent-has-course-enrollment") !== "0"
  );
  const [parentPermissionsSynced, setParentPermissionsSynced] = useState(
    () => localStorage.getItem("parent-permissions") !== null
  );

  const handleSwitchToStudent = async () => {
    // Prefer restored student session (no API, no PIN)
    try {
      const stored = sessionStorage.getItem(STUDENT_SESSION_KEY);
      if (stored) {
        const { token, role, userData, mustChangePassword, studentPermissions } = JSON.parse(stored);
        if (token && role) {
          localStorage.setItem("token", token);
          localStorage.setItem("role", role);
          if (userData) localStorage.setItem("userData", userData);
          if (mustChangePassword != null) localStorage.setItem("mustChangePassword", mustChangePassword);
          // Only overwrite when the saved session carried them. Sessions stored before
          // studentPermissions was saved must keep whatever is already in localStorage —
          // clearing it makes every student permission check fall open.
          if (studentPermissions != null) {
            localStorage.setItem("student-permissions", studentPermissions);
          }
          sessionStorage.removeItem(STUDENT_SESSION_KEY);
          toast.success("Switched to student portal.");
          navigate(`/${process.env.REACT_APP_STUDENT_PREFIX || "student"}`);
          window.location.reload();
          return;
        }
      }
    } catch (e) { /* ignore */ }

    setSwitchToStudentLoading(true);
    try {
      const res = await api.post("parent/switch-to-student");
      const data = res.data?.data;
      if (data?.access_token) {
        localStorage.setItem("token", data.access_token);
        localStorage.setItem("role", (data.role || "student").toString().toLowerCase());
        localStorage.setItem("userData", JSON.stringify(data));
        if (data.must_change_password !== undefined) {
          localStorage.setItem("mustChangePassword", data.must_change_password ? "1" : "0");
        }
        if (Array.isArray(data.permissions)) {
          localStorage.setItem("student-permissions", JSON.stringify(data.permissions));
        } else {
          localStorage.removeItem("student-permissions");
        }
        // Clear any leftover admin-token so student API calls use the correct scoped token
        localStorage.removeItem("admin-token");
        toast.success("Switched to student portal.");
        navigate(`/${process.env.REACT_APP_STUDENT_PREFIX || "student"}`);
        window.location.reload();
      } else {
        toast.error("Could not switch to student portal.");
      }
    } catch (err) {
      const msg = err.response?.data?.data?.error ?? err.response?.data?.message ?? "Could not switch to student portal.";
      toast.error(msg);
    } finally {
      setSwitchToStudentLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };//logout 

  const handleToggle = (label) => {
    setOpenMenus((prev) => ({
      ...prev,
      [label]: !prev[label],
    }));
  };

  // Helper function to check if parent menu should be highlighted
  const isParentActive = (item) => {
    if (item.path) {
      // Normalize both paths by removing trailing slash for comparison
      const currentPath = location.pathname.replace(/\/$/, '');
      const itemPath = item.path.replace(/\/$/, '');

      // Check if normalized paths match
      if (currentPath === itemPath) return true;
    }
    if (item.children) {
      return item.children.some(child => location.pathname === child.path);
    }
    return false;
  };
  const name = process.env.REACT_APP_PARENT_PREFIX;

  useEffect(() => {
    if (!isMobile) {
      setSidebarOpen(true);
      setMobileFull(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) setMobileFull(false);
  }, [location.pathname, isMobile]);

  const handleHamburger = () => {
    if (isMobile) {
      setMobileFull((prev) => !prev);
    } else {
      setSidebarOpen((prev) => !prev);
    }
  };

  const menuItems = useMemo(
    () => [
      {
        label: "Dashboard",
        path: `/${name}`,
        icon: <Dashboard />,
        permission: "parent-panel-view-dashboard",
      },
      {
        label: "Students",
        icon: <School />,
        children: [
          {
            label: "My Student",
            path: `/${name}/my-student-list`,
            icon: <People />,
            anyOf: ["parent-panel-view-my-students", "parent-panel-manage-students"],
          },
          {
            label: "Add Student",
            path: `/${name}/add-student`,
            icon: <PersonAdd />,
            permission: "parent-panel-manage-students",
          },
          {
            label: "Change Student Password",
            path: `/${name}/change-password`,
            icon: <Lock />,
            permission: "parent-panel-change-student-password",
          },
        ],
      },
      {
        label: "Progress",
        icon: <TrendingUp />,
        children: [
          {
            label: "End of Report",
            courseOnly: true,
            path: `/${name}/end-of-report`,
            icon: <Assessment />,
            permission: "parent-panel-view-end-of-report",
          },
          {
            label: "Readiness",
            path: `/${name}/readiness`,
            icon: <Insights />,
            anyOf: ["parent-panel-view-dashboard", "parent-panel-view-end-of-report"],
          },
          {
            label: "Course Target Area",
            courseOnly: true,
            path: `/${name}/course-target-area`,
            icon: <BarChart />,
            permission: "parent-panel-view-course-target-area",
          },
          {
            label: "Finished Test",
            courseOnly: true,
            path: `/${name}/finished-test`,
            icon: <Quiz />,
            permission: "parent-panel-view-finished-test",
          },
          {
            label: "Test Scores",
            courseOnly: true,
            path: `/${name}/test-scores`,
            icon: <Assessment />,
            permission: "parent-panel-view-test-scores",
          },
          {
            label: "Classes",
            path: `/${name}/classes`,
            icon: <Event />,
          },
          {
            label: "Study Coach Chats",
            path: `/${name}/assistant-chats`,
            icon: <AutoAwesome />,
          },
        ],
      },
      {
        label: "Student Results",
        icon: <Assessment />,
        children: [
          { label: "Mock Exam Result", path: `/${name}/mock-exam-results`, icon: <Quiz /> },
          { label: "Practice Paper Result", path: `/${name}/past-paper-results`, icon: <MenuBook /> },
          { label: "Worksheet Results", path: `/${name}/ai-answer-sheet`, icon: <FactCheck />, state: { openMyResults: true } },
        ],
      },
      {
        label: "Billing",
        path: `/${name}/billing`,
        icon: <ReceiptLong />,
        permission: "parent-panel-view-billing",
      },
      {
        // Everything bought for the family, with watermarked PDF downloads.
        label: "My Purchases",
        path: `/${name}/my-purchases`,
        icon: <ShoppingBag />,
        permission: "parent-panel-view-billing",
      },
      {
        label: "Notifications",
        path: `/${name}/notifications`,
        icon: <Inbox />,
        badgeCount: notificationCount,
        anyOf: ["parent-panel-view-dashboard", "parent-panel-view-announcements"],
      },
      {
        // Copies of the emails we sent this parent.
        label: "Messages",
        path: `/${name}/messages`,
        icon: <MailOutline />,
        badgeCount: messageCount,
        anyOf: ["parent-panel-view-dashboard", "parent-panel-view-announcements"],
      },
      {
        label: "Announcements",
        courseOnly: true,
        path: `/${name}/announcements`,
        icon: <Notifications />,
        badgeCount: announcementCount,
        permission: "parent-panel-view-announcements",
      },
      {
        label: "Certificates",
        path: `/${name}/certificates`,
        icon: <EmojiEvents />,
        permission: "parent-panel-view-certificates",
      },
      {
        label: "Setting",
        icon: <Settings />,
        children: [
          {
            label: "Billing Information",
            path: `/${name}/billing-information`,
            icon: <Home />,
            permission: "parent-panel-manage-billing-information",
          },
          {
            label: "Settings",
            path: `/${name}/settings`,
            icon: <Settings />,
            permission: "parent-panel-account-settings",
          },
        ],
      },
    ],
    [name, announcementCount, notificationCount, messageCount]
  );

  const filteredMenuItems = useMemo(() => {
    const has = (p) => parentPermissions.includes(p);
    const hasAny = (arr) => arr.some((x) => has(x));
    const visibleChild = (child) => {
      if (child.courseOnly && !hasCourseEnrollment) return false;
      if (child.anyOf) return hasAny(child.anyOf);
      if (child.permission) return has(child.permission);
      return true;
    };
    return menuItems
      .map((item) => {
        if (item.children) {
          const children = item.children.filter(visibleChild);
          if (children.length === 0) return null;
          return { ...item, children };
        }
        if (item.courseOnly && !hasCourseEnrollment) return null;
        if (item.anyOf) {
          if (!hasAny(item.anyOf)) return null;
          return item;
        }
        if (item.permission && !has(item.permission)) return null;
        return item;
      })
      .filter(Boolean);
  }, [menuItems, parentPermissions, hasCourseEnrollment]);

  useEffect(() => {
    if (process.env.REACT_APP_DISABLE_PARENT_PERMISSIONS_SYNC === "true") {
      setParentPermissionsSynced(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("parent/auth/permissions", {
          validateStatus: (status) => status === 200 || status === 404,
        });
        if (cancelled) return;
        if (res.status === 404) {
          localStorage.removeItem("parent-permissions");
          setParentPermissions([]);
          return;
        }
        const data = res.data;
        if (!data?.success || !Array.isArray(data.data?.permissions)) {
          localStorage.removeItem("parent-permissions");
          setParentPermissions([]);
          return;
        }
        const perms = data.data.permissions;
        localStorage.setItem("parent-permissions", JSON.stringify(perms));
        setParentPermissions(perms);
        // Older APIs omit the flag: keep every tab, as before.
        const enrolled = data.data.has_course_enrollment !== false;
        localStorage.setItem("parent-has-course-enrollment", enrolled ? "1" : "0");
        setHasCourseEnrollment(enrolled);
      } catch {
        localStorage.removeItem("parent-permissions");
        setParentPermissions([]);
      } finally {
        if (!cancelled) {
          setParentPermissionsSynced(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load user data from localStorage
  useEffect(() => {
    const storedUserData = localStorage.getItem('userData');
    if (storedUserData) {
      try {
        const parsedData = JSON.parse(storedUserData);
        setUserData(parsedData);
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
      }
    }
  }, []);

  useEffect(() => {
    const stateId = location.state?.studentId;
    if (!stateId) return;
    const parsedId = Number(stateId);
    if (!Number.isFinite(parsedId) || parsedId <= 0) return;
    setActiveStudentId(parsedId);
    sessionStorage.setItem(ACTIVE_PARENT_STUDENT_KEY, String(parsedId));
  }, [location.state]);

  const handlePracticeToggle = async (event) => {
    if (!activeStudentId) {
      toast.info("Select a student first to change practice mode.");
      return;
    }
    const nextMode = event.target.checked ? "testing" : "actual";
    try {
      await setMode(nextMode, activeStudentId);
      toast.success(nextMode === "testing" ? "Practice mode enabled." : "Actual mode enabled.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to change exam mode.");
    }
  };

  // Redirect parent to settings when must change password (first login after registration)
  useEffect(() => {
    const mustChange = localStorage.getItem("mustChangePassword") === "1";
    const settingsPath = `/${name}/settings`;
    if (mustChange && !location.pathname.startsWith(settingsPath)) {
      navigate(settingsPath);
    }
  }, [location.pathname, navigate, name]);

  // Unread emails for the Messages badge; fetched on its own so a failure leaves the others.
  const fetchMessageCount = () => {
    api.get("parent/messages/unread-count")
      .then((r) => setMessageCount(Number(r.data?.data?.unread_count) || 0))
      .catch(() => setMessageCount(0));
  };
  useEffect(() => {
    window.addEventListener("panel-messages-read", fetchMessageCount);
    return () => window.removeEventListener("panel-messages-read", fetchMessageCount);
  }, []);

  // Fetch unread announcement/notification count for badge (only show when user has unviewed)
  const fetchUnreadCount = async () => {
    fetchMessageCount();
    try {
      const [announcements, unread] = await Promise.all([
        api.get("parent/notifications/unread-announcement-count"),
        api.get("parent/notifications/unread-count"),
      ]);
      const announcementTotal = announcements.data?.success && announcements.data?.data?.count != null
        ? Number(announcements.data.data.count) || 0
        : 0;
      const unreadTotal = unread.data?.success && unread.data?.data?.count != null
        ? Number(unread.data.data.count) || 0
        : 0;
      setAnnouncementCount(announcementTotal);
      setNotificationCount(Math.max(0, unreadTotal - announcementTotal));
    } catch (error) {
      console.error("Error fetching unread count:", error);
      setAnnouncementCount(0);
      setNotificationCount(0);
    }
  };

  // When user opens Announcements page, mark all announcement notifications as read so badge shows 0
  useEffect(() => {
    if (location.pathname.startsWith(`/${name}/announcements`)) {
      api.post("parent/notifications/mark-all-announcements-read").then(() => {
        setAnnouncementCount(0);
        fetchUnreadCount();
      }).catch(() => {});
    } else {
      fetchUnreadCount();
    }
  }, [location.pathname, name]);

  // Auto-expand parent menu when child route is active
  useEffect(() => {
    const currentPath = location.pathname;
    const newOpenMenus = { ...openMenus };

    filteredMenuItems.forEach((item) => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => child.path === currentPath);
        if (hasActiveChild) {
          newOpenMenus[item.label] = true;
        }
      }
    });

    setOpenMenus(newOpenMenus);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const desktopDrawerWidth = sidebarOpen ? drawerWidth : MINI_DRAWER_WIDTH;

  const renderParentNav = (minified) => (
    <>
    {!minified && isCompactAppBar && practiceModeVisible && (
      <Tooltip title={PRACTICE_MODE_TOOLTIP} arrow>
        <Box sx={{
          mx: 1,
          mt: 2,
          px: 2,
          py: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 2,
          background: "rgba(255,255,255,0.7)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}>
          <Typography sx={{ fontWeight: 700, fontSize: "14px", color: "#111827" }}>
            Practice mode
          </Typography>
          <Switch
            size="small"
            checked={mockExamsMode === "testing"}
            onChange={handlePracticeToggle}
            disabled={settingMode}
          />
        </Box>
      </Tooltip>
    )}
    <List sx={{ pt: minified ? 1 : 2 }}>
      {filteredMenuItems.map((item) => (
        <Box key={item.label} sx={{ mb: 1 }}>
          {item.children ? (
            <>
              <Tooltip title={minified ? item.label : ""} placement="right" arrow>
                <ListItemButton
                  data-tour={item.label}
                  onClick={() => {
                    if (minified && item.children[0]?.path) {
                      const child = item.children[0];
                      navigate(child.path, child.state ? { state: child.state } : undefined);
                      if (isMobile) setMobileFull(false);
                    } else {
                      handleToggle(item.label);
                    }
                  }}
                  sx={{
                    mx: minified ? 0.5 : 1,
                    borderRadius: 2,
                    mb: 0.5,
                    justifyContent: minified ? "center" : "flex-start",
                    px: minified ? 1 : 2,
                    background: openMenus[item.label] || isParentActive(item)
                      ? "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
                      : "transparent",
                    color: openMenus[item.label] || isParentActive(item) ? "#fff" : "#111827",
                    "&:hover": {
                      background: openMenus[item.label] || isParentActive(item)
                        ? "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
                        : "rgba(102, 126, 234, 0.1)",
                      transform: minified ? "none" : "translateX(4px)",
                    },
                  }}
                >
                  <ListItemIcon sx={{
                    minWidth: minified ? 0 : 46,
                    justifyContent: minified ? "center" : "flex-start",
                  }}>
                    <Box sx={navIconTileSx(itemColor(item), openMenus[item.label] || isParentActive(item))}>
                      {item.icon}
                    </Box>
                  </ListItemIcon>
                  {!minified && (
                    <>
                      <ListItemText
                        primary={item.label}
                        sx={{ "& .MuiTypography-root": { fontWeight: 700, fontSize: "14px" } }}
                      />
                      {(openMenus[item.label] || isParentActive(item)) ? <ExpandLess sx={{ color: "#fff" }} /> : <ExpandMore sx={{ color: "#667eea" }} />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>
              {!minified && (
                <Collapse in={openMenus[item.label]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {item.children.map((child) => (
                      <ListItemButton
                        key={child.label}
                        sx={{
                          pl: 6,
                          mx: 1,
                          borderRadius: 2,
                          mb: 0.5,
                          background: location.pathname === child.path
                            ? "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)"
                            : "transparent",
                          color: location.pathname === child.path ? "#fff" : "#1f2937",
                          "&:hover": {
                            background: location.pathname === child.path
                              ? "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)"
                              : "rgba(59, 42, 159, 0.1)",
                            transform: "translateX(8px)",
                          },
                        }}
                        onClick={() => {
                          navigate(child.path, child.state ? { state: child.state } : undefined);
                          if (isMobile) setMobileFull(false);
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 38 }}>
                          <Box sx={navIconTileSx(itemColor(item), location.pathname === child.path, 26)}>
                            {child.icon}
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary={child.label}
                          sx={{ "& .MuiTypography-root": { fontWeight: 700, fontSize: "13px" } }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}
            </>
          ) : (
            <Tooltip title={minified ? item.label : ""} placement="right" arrow>
              <ListItemButton
                data-tour={item.label}
                sx={{
                  mx: minified ? 0.5 : 1,
                  borderRadius: 2,
                  mb: 0.5,
                  justifyContent: minified ? "center" : "flex-start",
                  px: minified ? 1 : 2,
                  background: location.pathname === item.path
                    ? "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
                    : "transparent",
                  color: location.pathname === item.path ? "#fff" : "#111827",
                  "&:hover": {
                    background: location.pathname === item.path
                      ? "linear-gradient(90deg, #667eea 0%, #764ba2 100%)"
                      : "rgba(102, 126, 234, 0.1)",
                    transform: minified ? "none" : "translateX(4px)",
                  },
                }}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileFull(false);
                }}
              >
                <ListItemIcon sx={{
                  minWidth: minified ? 0 : 46,
                  justifyContent: minified ? "center" : "flex-start",
                }}>
                  <Box sx={navIconTileSx(itemColor(item), location.pathname === item.path)}>
                    {item.badgeCount != null ? (
                      <Badge badgeContent={item.badgeCount} color="error" max={99} invisible={!item.badgeCount || location.pathname.startsWith(item.path)}>
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </Box>
                </ListItemIcon>
                {!minified && (
                  <ListItemText
                    primary={item.label}
                    sx={{ "& .MuiTypography-root": { fontWeight: 700, fontSize: "14px" } }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          )}
        </Box>
      ))}
    </List>
    </>
  );

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      "@supports (height: 100dvh)": { height: "100dvh" },
      overflow: "hidden",
    }}>
      <CssBaseline />

      {/* Show loader only when loading is true */}
      {loading && <LinearProgress sx={{ height: 4 }} />}

      {/* Container for AppBar and main layout */}
      <Box sx={{ display: "flex", flexGrow: 1, overflow: "hidden", minHeight: 0 }}>
        {/* Top App Bar */}
        <AppBar
          position="fixed"
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            background: "linear-gradient(90deg, #3B2A9F 0%, #D62926 100%)",
            boxShadow: "none",
            top: loading ? "4px" : 0,
            transition: "top 0.3s",
          }}
        >
          <Toolbar sx={{
            justifyContent: "space-between",
            px: { xs: 1.5, sm: 3 },
            minHeight: { xs: 56, sm: 70 },
            height: { xs: 56, sm: 70 },
            background: "rgba(255,255,255,0.05)",
            backdropFilter: "blur(10px)",
          }}>
            {/* Left side - Toggle Button, Logo and Title */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0, flex: 1 }}>
              {/* Sidebar Toggle Button */}
              <Tooltip title={isMobile ? (mobileFull ? "Close menu" : "Open menu") : (sidebarOpen ? "Collapse sidebar" : "Expand sidebar")} arrow>
                <IconButton
                  onClick={handleHamburger}
                  sx={{
                    background: "rgba(255,255,255,0.15)",
                    color: "#fff",
                    borderRadius: "12px",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255,255,255,0.2)",
                    "&:hover": {
                      background: "rgba(255,255,255,0.25)",
                      transform: "scale(1.05)",
                      boxShadow: "0 6px 20px rgba(0,0,0,0.3)"
                    },
                    transition: "all 0.3s ease"
                  }}
                >
                  <MenuIcon sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>

              <Box
                sx={{
                  background: "rgba(255,255,255,0.15)",
                  borderRadius: "12px",
                  p: { xs: 1, sm: 1.5 },
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: "0 4px 20px rgba(0,0,0,0.1)"
                }}
              >
                <Typography >📘</Typography>
              </Box>
              {/* No room for the title on phones beside the toggles. */}
              <Box sx={{ minWidth: 0, display: { xs: 'none', sm: 'block' } }}>
                <Typography
                  variant="h6"
                  noWrap
                  sx={{
                    color: "#fff",
                    fontWeight: 700,
                    fontSize: { xs: '1rem', sm: '1.375rem' },
                    textShadow: "0 2px 4px rgba(0,0,0,0.3)"
                  }}
                >
                  {CapitalizeFirstLetter(name)} Panel
                </Typography>
              </Box>
              {/* Phones: who is signed in, in the title's place. Shrinks to "…" first. */}
              <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                <Avatar sx={{ width: 28, height: 28, fontSize: '12px', fontWeight: 700, bgcolor: 'rgba(255,255,255,0.3)', flexShrink: 0 }}>
                  {userData?.full_name?.charAt(0).toUpperCase()}
                </Avatar>
                <Typography noWrap sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', minWidth: 0 }}>
                  {userData?.first_name || userData?.full_name?.split(" ")[0] || 'Parent'}
                </Typography>
              </Box>
            </Box>

            {/* Right side - Portal toggle (Student | Parent), User info and Logout */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 }, flexShrink: 0 }}>
              {parentPermissions.includes("parent-panel-switch-to-student") && (
              <PortalToggleSwitch
                value="parent"
                onSelectStudent={handleSwitchToStudent}
                onSelectParent={() => {}}
                disabled={switchToStudentLoading}
                size={isCompactAppBar ? "small" : "medium"}
              />
              )}
              {/* On phones Practice lives in the drawer instead (renderParentNav). */}
              {practiceModeVisible && !isCompactAppBar && (
              <Tooltip title={PRACTICE_MODE_TOOLTIP} arrow>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.4,
                  py: 0.55,
                  borderRadius: "12px",
                  background: "rgba(255,255,255,0.14)",
                  border: "1px solid rgba(255,255,255,0.16)",
                }}>
                  <Typography variant="caption" sx={{ color: "#fff", fontWeight: 700 }}>
                    Practice
                  </Typography>
                  <Switch
                    size="small"
                    checked={mockExamsMode === "testing"}
                    onChange={handlePracticeToggle}
                    disabled={settingMode}
                  />
                </Box>
              </Tooltip>
              )}
              {/* User Profile Section */}
              <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    background: "rgba(255,255,255,0.15)",
                    borderRadius: "50%",
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {userData?.full_name ? (
                    <Avatar sx={{
                      width: 32,
                      height: 32,
                      fontSize: '14px',
                      fontWeight: 700,
                      bgcolor: 'rgba(255,255,255,0.3)'
                    }}>
                      {userData.full_name.charAt(0).toUpperCase()}
                    </Avatar>
                  ) : (
                    <AccountCircle sx={{ color: "#fff", fontSize: 28 }} />
                  )}
                </Box>
                {/* Long names and emails end in "…" rather than widening the bar. */}
                <Box sx={{ textAlign: 'right', minWidth: 0, maxWidth: { sm: 160, md: 240 } }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: '14px'
                    }}
                  >
                    {userData?.full_name || 'Parent Portal'}
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      display: 'block',
                      color: "rgba(255,255,255,0.7)",
                      fontSize: '11px'
                    }}
                  >
                    {userData?.email || 'Welcome back!'}
                  </Typography>
                </Box>
              </Box>

              <IconButton
                onClick={handleLogout}
                aria-label="Logout"
                sx={{
                  display: { xs: 'inline-flex', sm: 'none' },
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "12px",
                  "&:hover": { background: "rgba(255,255,255,0.25)" },
                }}
              >
                <Logout />
              </IconButton>
              <Button
                onClick={handleLogout}
                variant="contained"
                size="medium"
                startIcon={<Logout />}
                sx={{
                  display: { xs: 'none', sm: 'inline-flex' },
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "12px",
                  px: 2.5,
                  py: 1,
                  fontWeight: 600,
                  fontSize: '13px',
                  textTransform: 'none',
                  backdropFilter: "blur(10px)",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.2)",
                  "&:hover": {
                    background: "rgba(255,255,255,0.25)",
                    transform: "translateY(-1px)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Logout
              </Button>
            </Box>
            <TesterPortalSwitcher />
          </Toolbar>
        </AppBar>


        {!isMobile && (
          <Drawer
            variant="permanent"
            sx={{
              width: desktopDrawerWidth,
              flexShrink: 0,
              alignSelf: "stretch",
              minHeight: 0,
              transition: "width 0.3s ease",
              [`& .MuiDrawer-paper`]: parentDrawerPaperSx(desktopDrawerWidth, appBarHeight),
            }}
          >
            {renderParentNav(!sidebarOpen)}
          </Drawer>
        )}
        {/* Icon rail on tablets only — on phones the hamburger opens the full drawer. */}
        {isMobile && !isCompactAppBar && (
          <Drawer
            variant="permanent"
            sx={{
              width: MINI_DRAWER_WIDTH,
              flexShrink: 0,
              alignSelf: "stretch",
              minHeight: 0,
              [`& .MuiDrawer-paper`]: parentDrawerPaperSx(MINI_DRAWER_WIDTH, appBarHeight),
            }}
          >
            {renderParentNav(true)}
          </Drawer>
        )}
        {isMobile && (
          <Drawer
            variant="temporary"
            open={mobileFull}
            onClose={() => setMobileFull(false)}
            ModalProps={{ keepMounted: true }}
            sx={{
              [`& .MuiDrawer-paper`]: {
                ...parentDrawerPaperSx(drawerWidth, appBarHeight),
                width: `min(${drawerWidth}px, 85vw)`,
              },
            }}
          >
            {renderParentNav(false)}
          </Drawer>
        )}

        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            minHeight: 0,
            minWidth: 0,
            bgcolor: "#f9f9f9",
            p: { xs: 1, sm: 2, md: 3 },
            mt: `${appBarHeight}px`,
            overflowY: "auto",
            overflowX: "hidden",
            // Custom scrollbar styling for main content
            "&::-webkit-scrollbar": {
              width: "10px",
            },
            "&::-webkit-scrollbar-track": {
              background: "rgba(0,0,0,0.05)",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)",
              borderRadius: "10px",
              border: "2px solid #f9f9f9",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background: "linear-gradient(180deg, #3B2A9F 0%, #D62926 100%)",
            },
            // Firefox scrollbar
            scrollbarWidth: "thin",
            scrollbarColor: "#667eea rgba(0,0,0,0.05)",
          }}
        >
          <Outlet context={{ parentPermissionsSynced }} />
        </Box>
        <AssistantDrawer role="parent" activeStudentId={activeStudentId} />
        <ParentWelcomeTour
          onRequestDrawer={isCompactAppBar ? setMobileFull : undefined}
          userId={userData?.id} firstName={userData?.first_name || userData?.full_name?.split(" ")[0]} />
      </Box>
    </Box>
  );
};

export default ParentLayout;
